import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { authService } from '../services/auth';
import { CheckCircle, XCircle, Loader, ArrowRight, Sparkles } from 'lucide-react';

const VerifyEmailPage = () => {
  const { uid, token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verify = async () => {
      try {
        await authService.verifyEmail({ uid, token });
        setStatus('success');
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage(error.response?.data?.error || 'El enlace de verificación ha expirado o es inválido.');
      }
    };

    if (uid && token) {
      verify();
    } else {
      setStatus('error');
      setMessage('El enlace de verificación está incompleto.');
    }
  }, [uid, token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4E1D2] to-[#FFE8D6] flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans">
      
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-[#2A9D8F]/10 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-[#E76F51]/10 rounded-full blur-3xl opacity-60"></div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4 transform rotate-3">
             <img src="/logo-symbol.png" alt="Logo" className="w-10 h-10 object-contain" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <h2 className="text-3xl font-bold text-[#1F7A6F] tracking-tight">
            EstetikFlow
          </h2>
          <p className="mt-2 text-sm text-[#2A9D8F]/80 font-medium">
            Gestión Inteligente para Clínicas
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white/90 backdrop-blur-sm py-10 px-6 shadow-2xl rounded-2xl sm:px-12 border border-white/50 relative overflow-hidden transition-all duration-300">
          
          {/* Top colored line */}
          <div className={`absolute top-0 left-0 right-0 h-1.5 ${
            status === 'success' ? 'bg-[#2A9D8F]' : 
            status === 'error' ? 'bg-red-500' : 
            'bg-gray-300 animate-pulse'
          }`}></div>

          {status === 'loading' && (
            <div className="flex flex-col items-center animate-fade-in text-center">
              <div className="mb-6 relative">
                <div className="absolute inset-0 bg-[#2A9D8F]/20 rounded-full animate-ping"></div>
                <div className="relative bg-white p-4 rounded-full shadow-md border border-[#2A9D8F]/10">
                  <Loader className="h-8 w-8 text-[#2A9D8F] animate-spin" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Verificando tu cuenta</h3>
              <p className="text-gray-500 max-w-xs mx-auto leading-relaxed">
                Estamos confirmando tu información para activar tu acceso. Por favor espera un momento...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center animate-fade-in text-center">
              <div className="mb-6">
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-50 border-4 border-green-100 shadow-sm">
                  <CheckCircle className="h-10 w-10 text-green-500" strokeWidth={2.5} />
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800 mb-2">¡Cuenta Verificada!</h3>
              <p className="text-gray-600 mb-8 max-w-xs mx-auto leading-relaxed">
                Tu correo electrónico ha sido confirmado exitosamente. Tu cuenta está activa y lista para usar.
              </p>
              
              <Link
                to="/app/login"
                className="group w-full flex items-center justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-[#2A9D8F]/20 text-base font-bold text-white bg-[#2A9D8F] hover:bg-[#23877a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F] transition-all transform hover:-translate-y-0.5 active:translate-y-0"
              >
                Ingresar al Sistema
                <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center animate-fade-in text-center">
              <div className="mb-6">
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-50 border-4 border-red-100 shadow-sm">
                  <XCircle className="h-10 w-10 text-red-500" strokeWidth={2.5} />
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-gray-800 mb-2">Error de Verificación</h3>
              <p className="text-gray-500 mb-8 max-w-sm mx-auto bg-red-50 p-3 rounded-lg border border-red-100 text-sm">
                {message}
              </p>
              
              <Link
                to="/app/login"
                className="w-full flex justify-center items-center py-3 px-4 rounded-xl text-sm font-medium text-[#2A9D8F] bg-[#2A9D8F]/10 hover:bg-[#2A9D8F]/20 transition-colors"
              >
                Volver al inicio
              </Link>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500/80 font-medium">
            &copy; {new Date().getFullYear()} EstetikFlow. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
